import React, { useMemo, useState } from 'react'

import { useToast } from '@wartek-id/toast-v2'

import {
  useAPIDeleteKinerjaHarian,
  useAPIGetKinerjaHarianLogs,
} from 'api/kinerja/kinerja-harian-ps'
import KinerjaLayout from 'app/KinerjaShared/components/KinerjaLayout'
import { getServerSidePropsWithToggle } from 'utils/toggle.server'
import { TOGGLE_KEYS } from 'configs/toggle-feature'
import PageGuardPSPegawai from 'app/KinerjaPSPegawaiPage/components/PageGuardPSPegawai'
import { KinerjaHarianListPage } from 'app/KinerjaHarian/pages/KinerjaHarianListPage'
import { KinerjaHarianRoleProvider } from 'app/KinerjaHarian/components/KinerjaHarianRoleProvider'
import { PageGuardKinerjaHarianPS } from 'app/KinerjaHarian/components/PageGuardKinerjaHarianPS'
import { KinerjaTipeProvider } from 'app/KinerjaShared/components/KinerjaTipeProvider'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'

import type { NextPageContext } from 'next'
import type { TToggleKeyValuePair } from 'configs/toggle-feature'

const PS_PEGAWAI_HARIAN_ROUTES = {
  base: PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH,
  list: `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/harian`,
  add: `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/harian/add`,
  edit: (id: string) =>
    `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/harian/edit/${id}`,
}

export const getServerSideProps = getServerSidePropsWithToggle({
  toggleKeys: TOGGLE_KEYS.filter((key) => key.includes('kinerja')),
  serverProps: async (
    context: NextPageContext,
    toggle: TToggleKeyValuePair
  ) => {
    const enabled = toggle?.['kinerja-ps-enabled'] || false
    const enabledAsPegawai = toggle?.['kinerja-ps-pegawai-enabled'] || false

    if (!enabled || !enabledAsPegawai) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      }
    }
  },
})

function Page() {
  const toast = useToast()
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' })

  const {
    data: kinerjaHarianLogsResponse,
    isLoading,
    isFetching,
  } = useAPIGetKinerjaHarianLogs({
    params: {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    },
    options: { useErrorBoundary: false },
  })

  const logs = useMemo(
    () => kinerjaHarianLogsResponse?.data ?? [],
    [kinerjaHarianLogsResponse?.data]
  )

  const { mutate: deleteKinerjaHarian } = useAPIDeleteKinerjaHarian()

  const handleDelete = (entryDate: string) => {
    deleteKinerjaHarian(
      { entryDate, eventType: 'DELETED' },
      {
        onSuccess: () => {
          toast({ message: 'Berhasil hapus kinerja!', type: 'success' })
        },
        onError: () => {
          toast({ message: 'Gagal menghapus Kinerja Harian. Coba lagi.' })
        },
      }
    )
  }

  return (
    <PageGuardPSPegawai>
      <KinerjaTipeProvider tipe="SPF">
        <KinerjaHarianRoleProvider role="PS">
          <PageGuardKinerjaHarianPS>
            <KinerjaHarianListPage
              isLoading={isLoading || isFetching}
              logs={logs}
              routes={PS_PEGAWAI_HARIAN_ROUTES}
              trackerData={{ tipe: 'ps' }}
              onDelete={handleDelete}
              onDateChange={(startDate, endDate) => {
                setDateRange({ startDate, endDate })
              }}
            />
          </PageGuardKinerjaHarianPS>
        </KinerjaHarianRoleProvider>
      </KinerjaTipeProvider>
    </PageGuardPSPegawai>
  )
}

Page.Layout = KinerjaLayout

Page.layoutOpts = {
  hasHeader: true,
  pageTitle: 'Pengelolaan Kinerja',
}

export default Page
